import * as angular from 'angular';
import * as _ from 'lodash';

import { DashboardThumbnailWidget, IWidget, IWidgetConfiguration, WidgetData } from '../../widgets';
import { GridsterOptions } from '../gridsterOptions';
import { Group, ComponentDefinition, IGridsterOptions, TagDefinition, IValueViewModel, Dashboard, DigitalSetSimpleDictionary, TagStringValueDto, Dictionary } from '../../models';
import { IRootScope } from '../../IRootScope';
import { IGroupSelectionService, IDateRangeService, IDashboardService, ILoggerService, IStoreService, IWidgetConfigurationService, ICurrentApplicationService, IStringValuesService } from '../../services';
import { StoreElement, Store } from '../../stateManagement';
import { IEditWidgetStateService } from './editWidgetState.service';
import { backButtonClick, toggleEditMode } from '../../common';
import { ILayoutWidgets, IWidgetWithTemplate } from '../../services/widgets';

interface IEditWidgetState {
    widgetList: IWidget[];
}

export interface IWidgetContextual {
    hideError?: boolean;
    migrated?: boolean;
    template: string;
}

export interface IEditWidgetParams extends angular.ui.IStateParamsService {
    dashboardGuid: string;
    widgetGuid: string;
    beginDate: string;
    endDate: string;
}

class EditWidgetState implements IEditWidgetState {
    contextual: IWidgetContextual;
    class: string;
    dashboardGuid: string;
    dashboards: Dashboard[];
    digitalSetDictionary: DigitalSetSimpleDictionary;
    formController: angular.IFormController;
    groups: Group[];
    isBindable: boolean;
    item: IWidget;
    options: IGridsterOptions;
    referenceGroupGuid: string;
    stringValues: TagStringValueDto[];
    // The object of widgets displayed (i.e. left, right, bottom).
    widgetList: IWidgetWithTemplate[];
    widgets: ILayoutWidgets;
    applicationGuid: string;
    tagsDictionary: Dictionary<TagDefinition>;
    viewModels: IValueViewModel[];
    viewModelsDictionary: Dictionary<IValueViewModel>;

    private _pristineItemConfiguration: IWidgetConfiguration;
    private _unsubscribe: (() => void)[] = [];

    static $inject = [
        '$scope',
        '$rootScope',
        '$state',
        '$stateParams',
        '$timeout',
        'storeService',
        'groupSelectionService',
        'dateRangeService',
        'dashboardService',
        'widgetConfigurationService',
        'loggerService',
        'editWidgetStateService',
        'currentApplicationService',
        'stringValuesService'
    ];
    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: IRootScope,
        private _$state: angular.ui.IStateService,
        private _$stateParams: IEditWidgetParams,
        private _$timeout: angular.ITimeoutService,
        private _storeService: IStoreService,
        private _groupSelectionService: IGroupSelectionService,
        private _dateRangeService: IDateRangeService,
        private _dashboardService: IDashboardService,
        private _widgetConfigurationService: IWidgetConfigurationService,
        private _loggerService: ILoggerService,
        private _editWidgetStateService: IEditWidgetStateService,
        private _currentApplicationService: ICurrentApplicationService,
        private _stringValuesService: IStringValuesService
    ) {
    }

    $onInit() {
        if (!this._$rootScope.isEditMode) {
            this._$rootScope.$broadcast(toggleEditMode);
        }

        this._unsubscribe.push(this._$rootScope.$on(backButtonClick, () => this.leave()));

        if (this._$stateParams.beginDate) {
            this._dateRangeService.beginDate(this._$stateParams.beginDate);
        }

        if (this._$stateParams.endDate) {
            this._dateRangeService.endDate(this._$stateParams.endDate);
        }

        this._dateRangeService.initialized(true);
        this.dashboardGuid = this._$stateParams.dashboardGuid;
        this.options = new GridsterOptions().options();
        this.widgetList = [];
        this.applicationGuid = this._currentApplicationService.getApplicationConfiguration()?.guid;

        this._dashboardService
            .getAll()
            .then(dashboards => this.refresh(dashboards))
            .catch(e => this._loggerService.logError(e));

        this._unsubscribe.push(this._storeService.register(
            store => this.refreshStore(store),
            [
                StoreElement.Groups,
                StoreElement.Tags,
                StoreElement.ValueViewModels,
                StoreElement.Dashboards,
                StoreElement.DigitalSets
            ]
        ));
    }

    $onDestroy() {
        this._unsubscribe.forEach(u => u());
    }

    save(): void {
        const toSave = {...this.item};
        delete toSave.data;
        const data = this.item.data;
        this._dashboardService
            .updateWidget(this.dashboardGuid, toSave)
            .then(dashboard => {
                this.formController.$setPristine();
                this.item = _.find(dashboard.configuration.widgets, (w: IWidget) => w.guid === this.item.guid);
                this.item.data = data;
                this._loggerService.logSuccess(`Widget ${this.item.name} saved successfully.`);
                this.forceReload();
            },
            () => {
                this._loggerService.logError(`Error while saving ${this.item.name} .`);
            });
    }

    cancel(): void {
        this.item.configuration = angular.copy(this._pristineItemConfiguration);
        this._$timeout(() => this.formController?.$setPristine());
    }

    private refreshStore(store: Store) {
        this.groups = _.values(store.groups.dictionary);
        this.tagsDictionary = store.tags.dictionary;
        this.viewModelsDictionary = store.valueViewModels.dictionary;
        this.viewModels = _.values(this.viewModelsDictionary);
        this.dashboards = _.values(store.dashboards.dictionary);
        this.digitalSetDictionary = store.digitalSetDictionary.dictionary;
        this._stringValuesService
            .getAll()
            .then(stringValues => this.stringValues = stringValues);
        // dashboard configuration are serialized in store, we must use non-serialized configuration
        //this.refresh(_.values(store.dashboards.dictionary));
    }

    private forceReload() {
        this._storeService.dispatchers().groups.getAll(true);
        this._storeService.dispatchers().valueViewModels.getAll(true);
        this._storeService.dispatchers().dashboards.getAll(true);
    }

    private refresh(dashboards: DashboardThumbnailWidget[]) {
        const beginDate = this._dateRangeService.beginDate() as string;
        const endDate = this._dateRangeService.endDate() as string;

        const dashboard = _.find(dashboards, d => d.guid === this.dashboardGuid);
        const item = _.find<IWidget>(dashboard.configuration.widgets, w => w.guid === this._$stateParams.widgetGuid);
        item.data = {} as WidgetData;
        this._pristineItemConfiguration = angular.copy(item.configuration);
        this.referenceGroupGuid = dashboard?.referenceGroup;
        this.item = item;
        const configuration = this._widgetConfigurationService.getWidgetConfiguration(this.item);

        this.widgets = configuration.widgets;
        this.widgets.right.hideHeaderOptions = true;
        this.contextual = configuration.contextual;
        this.class = configuration.class;
        const hasTypes = !!dashboard.groupTypes && dashboard.groupTypes.length > 0;
        const hasGroups = !!dashboard.dashboard.groupGuids && dashboard.dashboard.groupGuids.length > 0;
        this.isBindable = hasTypes || hasGroups;
        if (this.isBindable) {
            this._groupSelectionService.reselectGroup(dashboard.referenceGroup);
        }

        return this._editWidgetStateService
            .refreshSeries(this.item, beginDate, endDate, this.referenceGroupGuid, this.dashboardGuid)
            .then(data => {
                this._$scope.$watch(() => this.item.configuration,
                    (newValue, oldValue) => {
                        if (oldValue && newValue !== oldValue && this.formController) {
                            this.formController.$setDirty();
                        }
                        if (data) {
                            this._editWidgetStateService.refreshSeries(this.item, beginDate, endDate, this.referenceGroupGuid, this.dashboardGuid);
                        }
                    }, true);
            });
    }

    private leave() {
        this._$state.go('dashboard', { dashboardName: this.dashboardGuid, beginDate: this._$stateParams.beginDate, endDate: this._$stateParams.endDate });
    }
}

export const wtEditWidgetState: ComponentDefinition = {
    componentId: 'wtEditWidgetState',
    controller: EditWidgetState,
    template: require('./editWidget.component.html'),
    controllerAs: 'vm',
    bindings: {
    }
};